import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { getDealershipByToken } from '../../services/dealershipService';
import { dealershipService } from '../../services/dealershipService';
import VoucherCodeValidator from '../../components/ui/VoucherCodeValidator';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import Button from '../../components/ui/Button';

const VoucherVerificationPublic = () => {
  const params = useParams();
  const navigate = useNavigate();
  const [dealership, setDealership] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Extract dealership slug from the route parameter
  // The route is /:dealershipSlug_gutscheincheck, so the param name includes the suffix
  const dealershipSlugParam = params?.['dealershipSlug_gutscheincheck'] || params?.dealershipSlug;
  const token = params?.token;

  useEffect(() => {
    const verifyAccess = async () => {
      // Check if we have a slug-based URL (e.g., /stoll-gmbh-waldshut-tiengen_gutscheincheck)
      if (dealershipSlugParam) {
        try {
          // The parameter already contains the full slug without _gutscheincheck
          // because React Router captures it that way
          const dealershipData = await dealershipService?.getBySlug(dealershipSlugParam);
          setDealership(dealershipData);
        } catch (err) {
          setError('Partner nicht gefunden oder nicht aktiv');
        }
        setLoading(false);
        return;
      }

      // Fallback to token-based verification
      if (!token) {
        setError('Kein Verifizierungs-Token gefunden');
        setLoading(false);
        return;
      }

      const result = await getDealershipByToken(token);
      
      if (result?.success) {
        setDealership(result?.dealership);
      } else {
        setError(result?.error || 'Ungültiger oder abgelaufener Verifizierungs-Link');
      }
      
      setLoading(false);
    };

    verifyAccess();
  }, [token, dealershipSlugParam]);

  const handleValidation = (result) => {
    console.log('Voucher validated:', result);
    // Validation result is handled by VoucherCodeValidator component
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Verifiziere Zugang...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-card rounded-lg shadow-card p-8 text-center">
          <div className="w-16 h-16 bg-error/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="AlertCircle" size={32} color="var(--color-error)" />
          </div>
          <h2 className="text-xl font-heading font-semibold text-foreground mb-2">
            Zugriff verweigert
          </h2>
          <p className="text-muted-foreground mb-6">{error}</p>
          <Button
            variant="default"
            fullWidth
            iconName="Home"
            iconPosition="left"
            onClick={() => navigate('/')}
          >
            Zur Startseite
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Gutschein-Verifizierung | {dealership?.name} | GridBalance</title>
        <meta
          name="description"
          content={`Verifizieren Sie THG-Gutscheincodes für ${dealership?.name}`}
        />
      </Helmet>
      
      <div className="min-h-screen bg-background">
        <header className="bg-card border-b border-border sticky top-0 z-40">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                {dealership?.logo && (
                  <div 
                    className="w-12 h-12 rounded-lg overflow-hidden flex items-center justify-center border-2 border-border p-1"
                    style={{ backgroundColor: dealership?.logoBackgroundColor || '#FFFFFF' }}
                  >
                    <Image
                      src={dealership?.logo}
                      alt={dealership?.logo_alt || `Logo von ${dealership?.name}`}
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                )}
                <div>
                  <h1 className="text-xl font-heading font-bold text-foreground">
                    {dealership?.name}
                  </h1>
                  <p className="text-sm text-muted-foreground">
                    Gutschein-Verifizierung
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="Zap" size={20} color="var(--color-primary)" />
                </div>
                <span className="text-sm font-medium text-foreground hidden sm:inline">
                  Grid Balance
                </span>
              </div>
            </div>
          </div>
        </header>

        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 mb-8">
            <div className="flex items-start gap-3">
              <Icon name="Info" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-foreground mb-1">
                  Direkter Verifizierungszugang
                </p>
                <p className="text-xs text-muted-foreground">
                  Sie können Gutscheincodes direkt über diesen Link verifizieren, ohne sich anmelden zu müssen.
                  Dieser Link ist personalisiert für {dealership?.name}.
                </p>
              </div>
            </div>
          </div>

          <VoucherCodeValidator
            dealershipId={dealership?.id}
            onValidate={handleValidation}
          />

          <div className="mt-8 bg-card rounded-lg shadow-card p-6">
            <h3 className="text-lg font-heading font-semibold text-foreground mb-4">
              Partner-Informationen
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <Icon name="MapPin" size={20} color="var(--color-muted-foreground)" />
                <div>
                  <p className="text-sm font-medium text-foreground">Standort</p>
                  <p className="text-xs text-muted-foreground">
                    {dealership?.street}, {dealership?.postal_code} {dealership?.city}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Icon name="Phone" size={20} color="var(--color-muted-foreground)" />
                <div>
                  <p className="text-sm font-medium text-foreground">Telefon</p>
                  <p className="text-xs text-muted-foreground">
                    {dealership?.phone}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Icon name="Mail" size={20} color="var(--color-muted-foreground)" />
                <div>
                  <p className="text-sm font-medium text-foreground">E-Mail</p>
                  <p className="text-xs text-muted-foreground">
                    {dealership?.email}
                  </p>
                </div>
              </div>
              {dealership?.website && (
                <div className="flex items-center gap-3">
                  <Icon name="Globe" size={20} color="var(--color-muted-foreground)" />
                  <div>
                    <p className="text-sm font-medium text-foreground">Website</p>
                    <a 
                      href={dealership?.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-primary hover:underline"
                    >
                      {dealership?.website}
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="mt-8 bg-muted/30 rounded-lg p-6 border border-border">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <Icon name="Shield" size={20} color="var(--color-primary)" />
              </div>
              <div className="flex-1">
                <h3 className="text-base font-heading font-semibold text-foreground mb-2">
                  Sicherheitshinweis
                </h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <span className="text-primary font-bold">•</span>
                    <span>
                      Dieser Verifizierungs-Link ist nur für {dealership?.name} gültig
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary font-bold">•</span>
                    <span>
                      Alle Verifizierungen werden automatisch protokolliert
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary font-bold">•</span>
                    <span>
                      Gutscheincodes können nur einmal verifiziert werden
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary font-bold">•</span>
                    <span>
                      Bei Problemen kontaktieren Sie bitte den Grid Balance Support
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </main>

        <footer className="bg-card border-t border-border py-6 px-4 sm:px-6 lg:px-8 mt-12">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="Zap" size={20} color="var(--color-primary)" />
                </div>
                <span className="text-sm text-muted-foreground">
                  © {new Date()?.getFullYear()} Grid Balance. Alle Rechte vorbehalten.
                </span>
              </div>
              <div className="flex items-center gap-4 text-xs text-muted-foreground">
                <a href="mailto:oliver.wagner@gridbalance.de" className="hover:text-primary">
                  Support
                </a>
                <span>•</span>
                <a href="https://www.gridbalance.de" target="_blank" rel="noopener noreferrer" className="hover:text-primary">
                  www.gridbalance.de
                </a>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default VoucherVerificationPublic;